import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import {
  USER_ENDPOINTS,
  getEndpointInfo,
} from './apiEndPoints';
// Types
export interface UserProfileRole {
  id: string;
  name: string;
}

export interface UserProfile {
  user_id: string;
  uuid: string;
  auth0_id: string;
  full_name: string;
  birthday: string;
  phone_number: string;
  user_role: string;
  roles?: UserProfileRole[];
  kyc_status?: string;
  phone_verified?: boolean;
  first_login?: boolean;
  card_verified?: boolean;
  negotiateID?: number;
  isSocialLogin?: boolean;
  is_deleted?: boolean;
  is_blocked?: boolean;
  address: {
    country_name: string;
    state_name: string;
    city_name: string;
    postal_code: string;
    street_no_one: string;
    street_no_two: string;
    longitude: string;
    latitude: string;
  };
  profile_image: string;
  data_policy_accepted?: boolean;
}

interface UpdateUserRoleRequest {
  user_id: string;
  role_id: string;
}

export interface UpdateProfileRequest {
  fullName: string;
  birthday: string | null;
  contactNumber?: string;
  data_policy_accepted?: boolean;
  address: {
    country_name?: string;
    state_name?: string;
    city_name?: string;
    postalCode?: string;
    street1: string;
    street2?: string;
    latitude?: string;
    longitude?: string;
  };
}

interface WeightBasedPrice {
  id: number;
  weight: string;
  price: string;
  min_price: string;
  max_price: string;
}

interface WeightBasedPriceResponse {
  weight_based_prices: WeightBasedPrice[];
}

// Drop Off (Item Images Upload) Interfaces
export interface DropOffRequest {
  user_id: number | string;
  order_id: number | string;
  item_images: File[];
}

export interface DropOffResponse {
  message: string;
  uploadedImages: string[];
}

// Setting (Time Rules) Interfaces
export interface TimeRule {
  id: number;
  rule_name: string;
  description: string;
  time_in_minutes: number;
  time_in_hours: string;
}

export type SettingResponse = TimeRule[];

// Time Preferences Interfaces
// Distance Preferences Interfaces

// Update Password Interfaces
export interface UpdatePasswordRequest {
  user_id: string;
  newPassword: string;
  reauth_token?: string; // Fresh Auth0 token from re-authentication
}

export interface UpdatePasswordResponse {
  message: string;
}

// Delete Account Interfaces
export interface DeleteAccountRequest {
  reauth_token?: string; // Fresh Auth0 token from re-authentication
}

export interface DeleteAccountResponse {
  message: string;
}

// Issue Reasons Interfaces
export interface IssueReason {
  id: string;
  name: string;
  value: string;
  description?: string;
}

export interface IssueReasonsResponse {
  message: string;
  data: IssueReason[];
}

// Submit Issue Interfaces
export interface SubmitIssueRequest {
  user_id: string;
  correlationId: string;
  first_name: string;
  last_name: string;
  email: string;
  order_code: string;
  issue_reason_id: string;
  description: string;
  image_ids?: string[];
  type: string;
}

export interface SubmitIssueResponse {
  message: string;
  data: unknown;
}

// Upload Issue Images Interfaces
export interface UploadIssueImagesRequest {
  user_id: number | string;
  item_images: File[];
}

export interface UploadIssueImagesResponse {
  items: { id: string; url: string }[];
}

// Contact Interfaces
export interface ContactRequest {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  subject: string;
  message: string;
}

export interface ContactResponse {
  message: string;
  data: unknown;
}

// Default Roles Interfaces
export interface DefaultRole {
  id: string;
  name: string;
  description?: string;
}

export interface DefaultRolesResponse {
  roles: DefaultRole[];
}

// OTP Interfaces
export interface SendOtpRequest {
  user_id: string;
  phone_number: string;
}

export interface SendOtpResponse {
  message: string;
  data?: unknown;
}

export interface VerifyOtpRequest {
  user_id: string;
  phone_number: string;
  otp: string;
}

export interface VerifyOtpResponse {
  message: string;
  data?: unknown;
}

export interface ResendOtpRequest {
  user_id: string;
  phone_number: string;
}

export interface ResendOtpResponse {
  message: string;
  data?: unknown;
}


// Public API client (no auth token injected)
const publicApiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL_USER_V2,
  headers: { 'Content-Type': 'application/json' },
});

// Public Support Interfaces
export interface PublicIssueReason {
  id: string;
  name: string;
  is_active: boolean;
}

export interface PublicIssueReasonsResponse {
  message: string;
  data: PublicIssueReason[];
}

export interface PublicSubmitIssueRequest {
  first_name: string;
  last_name: string;
  email: string;
  issue_reason_id: string;
  description: string;
  recaptchaToken: string;
}

export interface PublicSubmitIssueResponse {
  message: string;
  data: unknown;
}

// Query keys
export const userKeys = {
  all: ['user'] as const,
  // Collections - use these for invalidating all items of a type
  allProfiles: ['user', 'profile'] as const,
  allTimePreferences: ['user', 'timePreferences'] as const,
  allDistancePreferences: ['user', 'distancePreferences'] as const,
  // Individual entities - use these for specific queries
  profile: (auth0Id: string) => ['user', 'profile', auth0Id] as const,
  userProfile: (auth0Id: string) => ['user', 'userProfile', auth0Id] as const,
  countries: ['user', 'countries'] as const,
  states: (countryId: string) => ['user', 'states', countryId] as const,
  cities: (stateId: string) => ['user', 'cities', stateId] as const,
  airports: (search: string) => ['user', 'airports', search] as const,
  weightPrices: ['user', 'weightPrices'] as const,
  timePreferences: (userId: number) => ['user', 'timePreferences', userId] as const,
  distancePreferences: (userId: number) => ['user', 'distancePreferences', userId] as const,
  settings: ['user', 'settings'] as const,
  offerExpirationSettings: ['user', 'offerExpirationSettings'] as const,
  issueReasons: ['user', 'issueReasons'] as const,
  publicIssueReasons: ['user', 'publicIssueReasons'] as const,
  defaultRoles: ['user', 'defaultRoles'] as const,
};

// API functions
const userApiClient = {
  // Base query function
  async request({ url, method = 'GET', body, headers }: {
    url: string;
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
  }) {
    try {
      const userService = getApiService(ServiceType.USER2);
      const response = await userService({
        url,
        method,
        data: body,
        headers: {
          'Content-Type': method === 'POST' || method === 'PUT' ? 'application/json' : undefined,
          ...headers,
        },
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  // API endpoint implementations
  getUserProfile: (auth0Id: string) =>
    userApiClient.request({
      url: USER_ENDPOINTS.GET_USER_PROFILE.path.replace(':auth0Id', auth0Id),
      method: 'GET',
    }),
  getUserProfileByID: (userId: string) =>
    userApiClient.request({
      url: USER_ENDPOINTS.GET_USER_PROFILE_BY_ID.path.replace(':userId', userId),
      method: 'GET',
    }),
  updateUserRole: (data: UpdateUserRoleRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.CHANGE_USER_ROLE);
    const payload = {
      user_id: String(data.user_id),
      role_id: String(data.role_id),
    };

    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: payload,
    });
  },

  uploadImagesForSender: async (formData: FormData) => {
    try {
      const userService = getApiService(ServiceType.USER2);
      const response = await userService({
        url: USER_ENDPOINTS.UPLOAD_IMAGES_V2.path,
        method: 'POST',
        data: formData,
        headers: {
          // Don't set Content-Type for FormData - let browser set it with boundary
          'Content-Type': undefined,
        },
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  updateUserProfile: (userId: string, data: UpdateProfileRequest) =>
    userApiClient.request({
      url: USER_ENDPOINTS.UPDATE_USER_PROFILE.path.replace(':userId', userId),
      method: 'PUT',
      body: data,
    }),

  uploadItemImages: async (data: DropOffRequest) => {
    try {
      const userService = getApiService(ServiceType.USER2);
      const formData = new FormData();

      formData.append('user_id', String(data.user_id));
      formData.append('order_id', String(data.order_id));

      // Append each image file to the FormData
      data.item_images.forEach((file) => {
        formData.append('item_images', file);
      });

      const response = await userService({
        url: USER_ENDPOINTS.UPLOAD_ITEM_IMAGES_V2.path,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': undefined,
        },
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  getTimeRulesSettings: () =>
    userApiClient.request({
      url: USER_ENDPOINTS.SETTING.path,
      method: 'GET',
    }),
  getPriceBasedOnWeight: () => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_PRICE_FROM_WEIGHT);
    return userApiClient.request({ url: endpoint.path });
  },
    updateUserAvatar: ({ userId, imageFile }: { userId: string; imageFile: string }) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.CHANGE_USER_AVATAR);
    const formData = new FormData();
    formData.append('avatar', imageFile);

    return userApiClient.request({
      url: endpoint.path.replace(':userId', userId.toString()),
      method: 'POST',
      body: formData,
    });
  },

  updatePassword: (data: UpdatePasswordRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.UPDATE_THE_PASSWORD);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: {
        user_id: data.user_id,
        newPassword: data.newPassword,
      },
      headers: data.reauth_token ? {
        'X-ReAuth-Token': data.reauth_token, // Pass re-auth token in custom header
      } : undefined,
    });
  },

  deleteAccount: (data?: DeleteAccountRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.DELETE_THE_ACCOUNT);
    return userApiClient.request({
      url: endpoint.path,
      method: 'DELETE',
      headers: data?.reauth_token ? {
        'X-ReAuth-Token': data.reauth_token, // Pass re-auth token in custom header
      } : undefined,
    });
  },

  getIssueReasons: (type?: string) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.ISSUE_REASONS);
    const url = type ? `${endpoint.path}?type=${encodeURIComponent(type)}` : endpoint.path;
    return userApiClient.request({
      url,
      method: 'GET',
    });
  },

  submitIssue: (data: SubmitIssueRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.SUBMIT_ISSUE);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: data,
    });
  },

  uploadIssueImages: async (data: UploadIssueImagesRequest) => {
    try {
      const userService = getApiService(ServiceType.USER2);
      const formData = new FormData();

      formData.append('user_id', String(data.user_id));

      // Append each image file to the FormData
      data.item_images.forEach((file) => {
        formData.append('item_images', file);
      });

      const response = await userService({
        url: USER_ENDPOINTS.UPLOAD_ISSUE_IMAGES.path,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': undefined,
        },
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  submitContact: (data: ContactRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.CONTACT);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: data,
    });
  },

  sendOtp: (data: SendOtpRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.SEND_OTP);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: {
        user_id: data.user_id,
        phone_number: data.phone_number,
      },
    });
  },

  verifyOtp: (data: VerifyOtpRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.VERIFY_OTP);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: {
        user_id: data.user_id,
        phone_number: data.phone_number,
        otp: data.otp,
      },
    });
  },

  resendOtp: (data: ResendOtpRequest) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.RESEND_OTP);
    return userApiClient.request({
      url: endpoint.path,
      method: 'POST',
      body: {
        user_id: data.user_id,
        phone_number: data.phone_number,
      },
    });
  },

  deleteProfileImage: (userId: string) => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.DELETE_PROFILE_IMAGE);
    return userApiClient.request({
      url: endpoint.path.replace(':userId', userId),
      method: 'DELETE',
    });
  },

  getDefaultRoles: async (): Promise<DefaultRolesResponse> => {
    const endpoint = getEndpointInfo(USER_ENDPOINTS.GET_DEFAULT_ROLES);
    const apiService = getApiService(endpoint.service);
    const response = await apiService({
      url: endpoint.path,
      method: 'GET',
    });
    return response.data;
  },

  getPlatformFeeSetting: () =>
    userApiClient.request({
      url: USER_ENDPOINTS.GET_PLATFORM_FEE_SETTING.path,
      method: 'GET',
    }),

  getOfferExpirationSettings: () =>
    userApiClient.request({
      url: USER_ENDPOINTS.GET_OFFER_EXPIRATION_SETTINGS.path,
      method: 'GET',
    }),
};

export const useGetUserProfileQuery = (auth0Id: string, options: Record<string, unknown> = {}) =>
  useQuery<UserProfile>({
    queryKey: userKeys.userProfile(auth0Id || ''),
    queryFn: () => userApiClient.getUserProfile(auth0Id),
    enabled: !!auth0Id && auth0Id.length > 0,
    ...options,
  });
export const useUploadImagesForSenderMutation = () => {
  return useMutation({
    mutationFn: (payload: unknown) => userApiClient.uploadImagesForSender(payload as FormData),
  });
};

// Update user profile mutation hook
export const useUpdateUserProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateProfileRequest }) =>
      userApiClient.updateUserProfile(userId, data),
    onSuccess: () => {
      // Invalidate all user queries so AuthInitializer re-fetches with updated profile data.
      // userKeys.allProfiles uses prefix ['user','profile'] which does NOT match the
      // ['user','userProfile',...] key used by useGetUserProfileQuery — invalidate the
      // whole ['user'] namespace to catch both shapes.
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};
export const useUpdateUserRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserRoleRequest) => userApiClient.updateUserRole(data),
    onSuccess: () => {
      // Invalidate profile queries so they refetch with the new role
      queryClient.invalidateQueries({ queryKey: userKeys.allProfiles });
    },
  });
};

// Upload item images mutation hook
export const useUploadItemImagesMutation = () => {
  return useMutation<DropOffResponse, Error, DropOffRequest>({
    mutationFn: (data: DropOffRequest) => userApiClient.uploadItemImages(data),
  });
};

// Get time rules settings query hook
export const useGetTimeRulesSettingsQuery = (options = {}) =>
  useQuery<SettingResponse>({
    queryKey: userKeys.settings,
    queryFn: () => userApiClient.getTimeRulesSettings(),
    ...options,
  });
export const useGetPriceBasedOnWeightQuery = (options = {}) =>
  useQuery<WeightBasedPriceResponse, Error, WeightBasedPrice[]>({
    queryKey: userKeys.weightPrices,
    queryFn: () => userApiClient.getPriceBasedOnWeight(),
    select: (data: any) => {
      if (Array.isArray(data)) return data;
      return data?.weight_based_prices ?? data?.data?.weight_based_prices;
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });
export const useUpdateUserAvatarMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, imageFile }: { userId: string; imageFile: string }) =>
      userApiClient.updateUserAvatar({ userId, imageFile }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

// Update password mutation hook
export const useUpdatePasswordMutation = () => {
  return useMutation<UpdatePasswordResponse, Error, UpdatePasswordRequest>({
    mutationFn: (data: UpdatePasswordRequest) => userApiClient.updatePassword(data),
  });
};

// Delete account mutation hook
export const useDeleteAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteAccountResponse, Error, DeleteAccountRequest | undefined>({
    mutationFn: (data?: DeleteAccountRequest) => userApiClient.deleteAccount(data),
    onSuccess: () => {
      // Clear all cached data on account deletion
      queryClient.clear();
    },
  });
};

// Delete profile image mutation hook
export const useDeleteProfileImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: (userId: string) => userApiClient.deleteProfileImage(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

// Get issue reasons query hook
export const useGetIssueReasonsQuery = (type?: string, options = {}) =>
  useQuery<IssueReasonsResponse>({
    queryKey: type ? [...userKeys.issueReasons, type] : userKeys.issueReasons,
    queryFn: () => userApiClient.getIssueReasons(type),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    ...options,
  });

// Submit issue mutation hook
export const useSubmitIssueMutation = () => {
  return useMutation<SubmitIssueResponse, Error, SubmitIssueRequest>({
    mutationFn: (data: SubmitIssueRequest) => userApiClient.submitIssue(data),
  });
};

// Upload issue images mutation hook
export const useUploadIssueImagesMutation = () => {
  return useMutation<UploadIssueImagesResponse, Error, UploadIssueImagesRequest>({
    mutationFn: (data: UploadIssueImagesRequest) => userApiClient.uploadIssueImages(data),
  });
};

// Send OTP mutation hook
export const useSendOtpMutation = () => {
  return useMutation<SendOtpResponse, Error, SendOtpRequest>({
    mutationFn: (data: SendOtpRequest) => userApiClient.sendOtp(data),
  });
};

// Verify OTP mutation hook
export const useVerifyOtpMutation = () => {
  return useMutation<VerifyOtpResponse, Error, VerifyOtpRequest>({
    mutationFn: (data: VerifyOtpRequest) => userApiClient.verifyOtp(data),
  });
};

// Resend OTP mutation hook
export const useResendOtpMutation = () => {
  return useMutation<ResendOtpResponse, Error, ResendOtpRequest>({
    mutationFn: (data: ResendOtpRequest) => userApiClient.resendOtp(data),
  });
};

// Public: Get issue reasons (no auth required)
export const useGetPublicIssueReasonsQuery = (options = {}) =>
  useQuery<PublicIssueReasonsResponse>({
    queryKey: userKeys.publicIssueReasons,
    queryFn: async () => {
      const response = await publicApiClient.get('/user/support/get-reasons');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });

// Get default roles query hook
export const useGetDefaultRolesQuery = (options = {}) =>
  useQuery<DefaultRolesResponse>({
    queryKey: userKeys.defaultRoles,
    queryFn: () => userApiClient.getDefaultRoles(),
    staleTime: 60 * 60 * 1000, // Cache for 1 hour
    gcTime: 60 * 60 * 1000,
    ...options,
  });

// Public: Submit support issue (no auth required)
export const usePublicSubmitIssueMutation = () =>
  useMutation<PublicSubmitIssueResponse, Error, PublicSubmitIssueRequest>({
    mutationFn: async (data: PublicSubmitIssueRequest) => {
      const response = await publicApiClient.post('/user/support/create-issue', data);
      return response.data;
    },
  });

export interface PlatformFeeSetting {
  platform_fee_percentage: string;
  platform_fee_flat: string;
  first_premium_price: string;
  price_upper_limit: string;
  min_payout_amount: string;
}

export const useGetPlatformFeeSettingQuery = (options = {}) =>
  useQuery<PlatformFeeSetting>({
    queryKey: ['user', 'platformFeeSetting'],
    queryFn: () => userApiClient.getPlatformFeeSetting(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });

export interface OfferExpirationOption {
  days: number;
  is_default: boolean;
}

export interface OfferExpirationSettingsResponse {
  options: OfferExpirationOption[];
  default_days: number;
}

export const useGetOfferExpirationSettingsQuery = (options = {}) =>
  useQuery<OfferExpirationSettingsResponse>({
    queryKey: userKeys.offerExpirationSettings,
    queryFn: () => userApiClient.getOfferExpirationSettings(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });